import React, { useState, useEffect, useRef } from 'react';
import { Input, List, Card, Typography, Tag, Empty, Space, Button } from 'antd';
import { SearchOutlined, CloseOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { stationOwnerAPI } from '../services/api';

const { Text } = Typography;
const { Search } = Input;

interface SearchResult {
  type: 'station' | 'campaign' | 'customer' | 'invoice' | 'employee' | 'supplier';
  id: string;
  title: string;
  description: string;
  route: string;
}

const GlobalSearch: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (searchTerm.length >= 2) {
      performSearch(searchTerm);
    } else {
      setResults([]);
    }
  }, [searchTerm]);

  const performSearch = async (term: string) => {
    setLoading(true);
    try {
      const allResults: SearchResult[] = [];

      // İstasyonları ara
      try {
        const stations = await stationOwnerAPI.getStations();
        const stationResults = stations
          .filter((s: any) => 
            s.name?.toLowerCase().includes(term.toLowerCase()) ||
            s.address?.toLowerCase().includes(term.toLowerCase())
          )
          .map((s: any) => ({
            type: 'station' as const,
            id: s._id,
            title: s.name,
            description: s.address,
            route: '/panel/stations'
          }));
        allResults.push(...stationResults);
      } catch (e) {}

      // Kampanyaları ara
      try {
        const campaigns = await stationOwnerAPI.getCampaigns();
        const campaignResults = campaigns
          .filter((c: any) =>
            c.title?.toLowerCase().includes(term.toLowerCase()) ||
            c.description?.toLowerCase().includes(term.toLowerCase())
          )
          .map((c: any) => ({
            type: 'campaign' as const,
            id: c._id,
            title: c.title,
            description: c.description || '',
            route: '/panel/campaigns'
          }));
        allResults.push(...campaignResults);
      } catch (e) {}

      // Müşterileri ara
      try {
        const customers = await stationOwnerAPI.getCustomers();
        const customerResults = customers
          .filter((c: any) =>
            `${c.firstName} ${c.lastName}`.toLowerCase().includes(term.toLowerCase()) ||
            c.email?.toLowerCase().includes(term.toLowerCase()) ||
            c.phone?.includes(term)
          )
          .map((c: any) => ({
            type: 'customer' as const,
            id: c._id,
            title: `${c.firstName} ${c.lastName}`,
            description: c.email || c.phone || '',
            route: '/panel/customers'
          }));
        allResults.push(...customerResults);
      } catch (e) {}

      setResults(allResults.slice(0, 10)); // İlk 10 sonuç
      setVisible(true);
    } catch (error) {
      console.error('Arama hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResultClick = (result: SearchResult) => {
    navigate(result.route);
    setVisible(false);
    setSearchTerm('');
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      station: 'İstasyon',
      campaign: 'Kampanya',
      customer: 'Müşteri',
      invoice: 'Fatura',
      employee: 'Personel',
      supplier: 'Tedarikçi'
    };
    return labels[type] || type;
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      station: 'blue',
      campaign: 'green',
      customer: 'orange',
      invoice: 'purple',
      employee: 'cyan',
      supplier: 'magenta'
    };
    return colors[type] || 'default';
  };

  return (
    <div ref={searchRef} style={{ position: 'relative', width: '100%', maxWidth: 400 }}>
      <Search
        placeholder="Ara... (İstasyon, Kampanya, Müşteri)"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onFocus={() => searchTerm.length >= 2 && setVisible(true)}
        loading={loading}
        allowClear
        style={{ width: '100%' }}
      />
      
      {visible && results.length > 0 && (
        <Card
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000,
            marginTop: 8,
            maxHeight: 400,
            overflowY: 'auto'
          }}
          bodyStyle={{ padding: '8px 0' }}
        >
          <List
            dataSource={results}
            renderItem={(item) => (
              <List.Item
                style={{ cursor: 'pointer', padding: '8px 16px' }}
                onClick={() => handleResultClick(item)}
              >
                <List.Item.Meta
                  title={
                    <Space>
                      <Text strong>{item.title}</Text>
                      <Tag color={getTypeColor(item.type)}>
                        {getTypeLabel(item.type)}
                      </Tag>
                    </Space>
                  }
                  description={<Text type="secondary" ellipsis>{item.description}</Text>}
                />
              </List.Item>
            )}
          />
        </Card>
      )}

      {visible && searchTerm.length >= 2 && results.length === 0 && !loading && (
        <Card
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000,
            marginTop: 8
          }}
        >
          <Empty description="Sonuç bulunamadı" />
        </Card>
      )}
    </div>
  );
};

export default GlobalSearch;

